package com.eladfinish.bible.contest.screens.pairMatching.ui

import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.ui.Modifier
import androidx.compose.ui.draganddrop.DragAndDropSourceModifierNode
import androidx.compose.ui.draganddrop.DragAndDropTransferData
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.input.pointer.SuspendingPointerInputModifierNode
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.node.DelegatingNode
import androidx.compose.ui.node.LayoutAwareModifierNode
import androidx.compose.ui.node.ModifierNodeElement
import androidx.compose.ui.platform.InspectorInfo
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.toSize

/**
 * Like [androidx.compose.foundation.draganddrop.dragAndDropSource], but starts the drag the moment
 * the user touches the element (touch-down) instead of after a long-press.
 *
 * The built-in `dragAndDropSource` hardcodes a long-press start detector for touch input and does
 * not expose a way to override it (foundation 1.11.1). This re-implements the same node wiring but
 * uses [awaitFirstDown] so the transfer begins immediately.
 *
 * Cross-platform — relies only on the public [DragAndDropSourceModifierNode] API.
 *
 * @param drawDragDecoration draws the drag shadow (typically a snapshot of the dragged item).
 * @param transferData builds the payload; return null to abort the drag.
 */
fun Modifier.dragAndDropSourceOnTouch(
    drawDragDecoration: DrawScope.() -> Unit,
    transferData: (Offset) -> DragAndDropTransferData?,
): Modifier = this then DragSourceOnTouchElement(drawDragDecoration, transferData)

private class DragSourceOnTouchElement(
    val drawDragDecoration: DrawScope.() -> Unit,
    val transferData: (Offset) -> DragAndDropTransferData?,
) : ModifierNodeElement<DragSourceOnTouchNode>() {

    override fun create() = DragSourceOnTouchNode(drawDragDecoration, transferData)

    override fun update(node: DragSourceOnTouchNode) {
        node.drawDragDecoration = drawDragDecoration
        node.transferData = transferData
    }

    override fun InspectorInfo.inspectableProperties() {
        name = "dragAndDropSourceOnTouch"
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is DragSourceOnTouchElement) return false
        if (drawDragDecoration !== other.drawDragDecoration) return false
        if (transferData !== other.transferData) return false
        return true
    }

    override fun hashCode(): Int = 31 * drawDragDecoration.hashCode() + transferData.hashCode()
}

private class DragSourceOnTouchNode(
    var drawDragDecoration: DrawScope.() -> Unit,
    var transferData: (Offset) -> DragAndDropTransferData?,
) : DelegatingNode(), LayoutAwareModifierNode {

    private var size: IntSize = IntSize.Zero

    private val sourceNode =
        delegate(
            DragAndDropSourceModifierNode { offset ->
                transferData(offset)?.let { data ->
                    startDragAndDropTransfer(
                        transferData = data,
                        decorationSize = size.toSize(),
                        drawDragDecoration = drawDragDecoration,
                    )
                }
            }
        )

    override fun onAttach() {
        // On some platforms (e.g. iOS) the platform itself initiates the drag and manually
        // requesting a transfer is unsupported (it throws). Only attach our touch detector when
        // the environment expects an explicit request (Android). Elsewhere the delegated
        // [sourceNode] still works — the platform starts the drag with its own gesture.
        if (sourceNode.isRequestDragAndDropTransferRequired) {
            delegate(
                SuspendingPointerInputModifierNode {
                    awaitEachGesture {
                        val down = awaitFirstDown(requireUnconsumed = false)
                        sourceNode.requestDragAndDropTransfer(down.position)
                    }
                }
            )
        }
    }

    // Forward layout callbacks to the delegated source node — it needs its own size for the
    // hit-test that Compose performs before invoking onStartTransfer.
    override fun onPlaced(coordinates: LayoutCoordinates) {
        sourceNode.onPlaced(coordinates)
    }

    override fun onRemeasured(size: IntSize) {
        this.size = size
        sourceNode.onRemeasured(size)
    }
}
